import { Box, Typography, Select, MenuItem } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { FC } from 'react';
import { formatDate } from '@/lib/utils/dateUtils';
import DetailField from '@/components/common/DetailField';
import { OutlinedPaper, FlexWrapBox } from '@/components/common/StyledComponents';
import { SampleActivityDto } from '@/api/ApiClient';
import { getVisibilityTypeDisplay, VISIBILITY_OPTIONS } from '@/lib/types/visibilityTypes';

import { useUserResolution } from '@/lib/options/useUserResolution';

interface BasicActivityInfoProps {
  activity: SampleActivityDto;
  isReadOnly: boolean;
  isSubmitted: boolean;
  handleModification: (modified: Partial<SampleActivityDto>) => void;
  convertDateForBackend: (date: Date | null) => Date | undefined;
  hideVisibility?: boolean; // Hide visibility field for readonly activities
}

const BasicActivityInfo: FC<BasicActivityInfoProps> = ({
  activity,
  isReadOnly,
  isSubmitted,
  handleModification,
  convertDateForBackend,
  hideVisibility = false,
}) => {
  const { resolveUser } = useUserResolution();

  return (
    <>
      <OutlinedPaper elevation={0} variant="outlined">
        <FlexWrapBox>
          {/* Visibility */}
          {!hideVisibility ? (
            <DetailField
              label="Visibility"
              value={activity.visibility ? getVisibilityTypeDisplay(activity.visibility) : 'N/A'}
              isReadOnly={isReadOnly}
            >
              <Select
                value={activity.visibility ?? ''}
                onChange={(e) => handleModification({ visibility: e.target.value })}
                fullWidth
                size="small"
              >
                {VISIBILITY_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {getVisibilityTypeDisplay(value)}
                  </MenuItem>
                ))}
              </Select>
            </DetailField>
          ) : null}

          <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
            <Typography variant="subtitle2" color="text.secondary">
              User
            </Typography>
            <Typography variant="body1">{activity.userId ? resolveUser(activity.userId) : 'N/A'}</Typography>
          </Box>

          {/* Date */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DetailField
              label="Date"
              value={activity.date ? formatDate(activity.date) : 'N/A'}
              isReadOnly={isReadOnly || isSubmitted}
              required
            >
              <DatePicker
                value={activity.date ? new Date(activity.date) : null}
                onChange={(newValue) => {
                  handleModification({
                    date: convertDateForBackend(newValue),
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </DetailField>
          </LocalizationProvider>
        </FlexWrapBox>
      </OutlinedPaper>
    </>
  );
};

export default BasicActivityInfo;
